// Google reviews for the TearDrop listing, read from the server's public
// /reviews route (Places API, cached 6h server side). Places only returns up
// to 5 reviews and has no reply support, so replying and the full list stay
// on Google Maps; this page is the at-a-glance view plus the review link to
// send to customers.
import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/auth';
import { Button, Card, PageHeader } from '../lib/ui';

const STAR_COLOR = '#f5a623';

function Stars({ rating, size = 14 }) {
  if (typeof rating !== 'number') return null;
  const pct = Math.max(0, Math.min(100, (rating / 5) * 100));
  return (
    <span aria-label={`${rating} out of 5 stars`} style={{ position: 'relative', display: 'inline-block', fontSize: size, lineHeight: 1, letterSpacing: 2 }}>
      <span style={{ color: 'var(--border)' }}>★★★★★</span>
      <span style={{ position: 'absolute', inset: 0, width: `${pct}%`, overflow: 'hidden', whiteSpace: 'nowrap', color: STAR_COLOR }}>★★★★★</span>
    </span>
  );
}

function ReviewCard({ review: r }) {
  return (
    <Card style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {r.profilePhotoUrl ? (
          <img src={r.profilePhotoUrl} alt="" referrerPolicy="no-referrer" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
        ) : (
          <span style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--panel-2)', display: 'grid', placeItems: 'center', fontWeight: 700, color: 'var(--muted)', flexShrink: 0 }}>
            {(r.author || '?').charAt(0).toUpperCase()}
          </span>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
          <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)' }}>{r.author || 'Google user'}</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Stars rating={r.rating} size={12} />
            <span style={{ fontSize: 12, color: 'var(--muted)' }}>{r.relativeTimeDescription}</span>
          </span>
        </div>
      </div>
      {r.text
        ? <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5, color: 'var(--text)', whiteSpace: 'pre-line' }}>{r.text}</p>
        : <p style={{ margin: 0, fontSize: 13, color: 'var(--muted)', fontStyle: 'italic' }}>Rating only, no written review.</p>}
    </Card>
  );
}

export default function ReviewsPage() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr]         = useState('');
  const [copied, setCopied]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const r = await api.get('/reviews');
      setData(r.data);
    } catch {
      setErr('Failed to load Google reviews');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(data.writeReviewUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setErr('Could not copy the review link');
    }
  };

  const reviews = data?.reviews || [];
  const total = data?.userRatingsTotal || 0;

  return (
    <div className="dashboard-container">
      <PageHeader
        title="Reviews"
        subtitle="What customers say on Google"
        actions={(
          <>
            <Button onClick={load} disabled={loading}>{loading ? 'Loading…' : 'Refresh'}</Button>
            {data?.mapsUrl && (
              <Button onClick={() => window.open(data.mapsUrl, '_blank', 'noreferrer')}>Open on Google Maps ↗</Button>
            )}
            {data?.writeReviewUrl && (
              <Button variant="primary" onClick={copyLink}>{copied ? 'Copied ✓' : 'Copy review link'}</Button>
            )}
          </>
        )}
      />

      {err && <div style={{ padding: 10, background: '#fff5f5', border: '1px solid #f5c6cb', marginBottom: 12, borderRadius: 'var(--r-lg)', fontSize: 13, color: '#b00020' }}>{err}</div>}

      {data && (
        <>
          <Card style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 40, fontWeight: 700, lineHeight: 1, color: 'var(--text)' }}>
              {typeof data.rating === 'number' ? data.rating.toFixed(1) : '–'}
            </span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <Stars rating={data.rating} size={18} />
              <span style={{ fontSize: 13, color: 'var(--muted)' }}>
                {total} Google review{total === 1 ? '' : 's'}
                {total > reviews.length && ` · showing the ${reviews.length} Google shares, see the rest on Maps`}
              </span>
            </div>
          </Card>

          {reviews.length > 0 ? (
            <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
              {reviews.map((r, i) => <ReviewCard review={r} key={i} />)}
            </div>
          ) : (
            <Card style={{ fontSize: 13, color: 'var(--muted)' }}>
              No reviews came back. If you have reviews on Google, check GOOGLE_PLACES_API_KEY and GOOGLE_PLACE_ID are set on the server.
            </Card>
          )}

          <p style={{ marginTop: 14, fontSize: 12, color: 'var(--muted)' }}>
            The server refreshes from Google every 6 hours, so a brand-new review can take a while to appear here. Replies are done on Google Maps.
          </p>
        </>
      )}
    </div>
  );
}
